import React, { useEffect, useRef, useState } from 'react';
import logo from '../../assets/logos/logo.png';
import { FiChevronDown, FiEye } from 'react-icons/fi';

const AdminHeader = ({
  isLoading,
  handleRefreshClick,
  handleOpenEmailPopup,
  handleLogout,
  tabs = [],
  activeTab,
  onTabChange,
}) => {
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const headerRef = useRef(null);

  // Add scroll event listener to check if header is sticky
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerPosition = headerRef.current.getBoundingClientRect();
        setIsHeaderSticky(headerPosition.top === 0);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div
      ref={headerRef}
      className={`sticky top-0 z-10 bg-white transition-shadow duration-300 ${
        isHeaderSticky ? 'shadow-md' : ''
      }`}>
      <div className="container mx-auto py-4 md:py-6 px-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:flex-wrap gap-4 md:gap-6">
          {/* Left side - Branding, heading and subheading */}
          <div className="w-auto min-w-0 max-w-full">
            <img
              src={logo}
              alt="ORIENT’EST — La Région Grand Est"
              className="block w-[280px] md:w-[340px] max-w-full h-auto mb-3 md:mb-4"
            />
            <h1 className="font-poppins text-lg md:text-2xl font-extrabold text-teal-40">
              PLATEFORME INSCRIPTION
            </h1>
            <p className="font-poppins text-sm md:text-lg text-gray-10">GROUPES GRAND EST 2026</p>
          </div>

          {/* Right side - Buttons */}
          <div className="flex w-full md:w-auto flex-wrap items-center gap-2 md:gap-4 max-w-full">
            {tabs.length > 0 && onTabChange && (
              <div
                className={`relative order-last w-full md:order-none md:w-auto max-w-full ${isHeaderSticky ? 'visible' : 'invisible max-md:hidden pointer-events-none'}`}>
                <FiEye
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-10 pointer-events-none"
                  aria-hidden="true"
                />
                <select
                  aria-label="Vue du tableau de bord"
                  value={activeTab}
                  onChange={(event) => {
                    const tab = tabs.find((item) => item.id === event.target.value);
                    if (tab) onTabChange(tab.id, tab.type);
                  }}
                  className="w-full md:w-auto max-w-full appearance-none bg-teal-30 text-blue-10 rounded-xl pl-10 pr-10 py-3 text-sm font-semibold cursor-pointer border-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-10 focus-visible:outline-offset-2">
                  {tabs.map((tab) => (
                    <option key={tab.id} value={tab.id}>
                      {tab.label}
                    </option>
                  ))}
                </select>
                <FiChevronDown
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-10 pointer-events-none"
                  aria-hidden="true"
                />
              </div>
            )}
            <button
              className={`${isLoading ? 'animate-spin' : ''} bg-teal-30 text-blue-10 hover:scale-105 active:scale-95 transition-all duration-300 p-3 rounded-full`}
              title="Refresh"
              onClick={handleRefreshClick}
              disabled={isLoading}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
            {/* Only render the email button if the handler is provided */}
            {handleOpenEmailPopup && (
              <button
                className="bg-teal-30 text-blue-10 hover:scale-105 active:scale-95 transition-all duration-300 p-3 rounded-full"
                title="Email"
                onClick={handleOpenEmailPopup}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </button>
            )}
            <button
              className="bg-blue-10 text-white hover:scale-105 active:scale-95 transition-all duration-300 p-3 px-3 md:px-5 rounded-xl flex items-center gap-2 text-xs md:text-base ml-auto md:ml-0"
              title="Logout"
              onClick={handleLogout}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span className="font-bold">DECONNEXION</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHeader;
